import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { TagComponent, TagColor } from "../../tag/tag.component";
import { TooltipModule } from "primeng/tooltip";
import { IEffectiveFacilityPolicy } from "types";
import {
  buildClosureTooltipText,
  ClosureStatusType,
  getMostRelevantClosure,
  getMostRelevantClosureForPeriod,
  getClosureLabelFromPolicy,
  getClosureLevelFromPolicy,
  getClosureStatusLabel,
} from "shared";

const CLOSURE_STATUS_COLORS: Record<ClosureStatusType, TagColor> = {
  [ClosureStatusType.CLOSED]: "orange",
  [ClosureStatusType.UPCOMING]: "blue",
  [ClosureStatusType.NO_CLOSURES]: "gray",
};

@Component({
  selector: "db-closure-status-tag",
  standalone: true,
  imports: [TagComponent, TooltipModule],
  templateUrl: "./closure-status-tag.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClosureStatusTagComponent {
  readonly closures = input<IEffectiveFacilityPolicy[] | null | undefined>(null);
  readonly timezone = input<string | undefined>(undefined);
  /** For meeting rooms: pass isActive (true/false) instead of closures */
  readonly isActive = input<boolean>();
  readonly isMeetingRoom = computed(() => this.isActive() !== undefined);
  /**
   * There are two variants of the component:
   * - "client": Used for showing effective closure status (only when there is effective facility policy) - it's gray with icon and specific context on what is closed e.g. "Area closed", "Floor closed", "Resource closed".
   * - "admin": Used for showing closure status in admin views - it's colored based on the status (closed, upcoming, no closures).
   */
  readonly variant = input<"client" | "admin">("admin");

  /**
   * Optional period start time for checking closures against a specific time range.
   * When both periodStart and periodEnd are provided, closures are checked for overlap
   * with this period instead of checking against "now".
   * Useful for booking views where you need to check if a closure affects a specific booking time.
   */
  readonly periodStart = input<string | number | undefined>(undefined);
  /**
   * Optional period end time for checking closures against a specific time range.
   * Must be used together with periodStart.
   */
  readonly periodEnd = input<string | number | undefined>(undefined);

  readonly status = computed(() => {
    // Meeting Rooms
    if (this.isMeetingRoom()) {
      return this.isActive() ? ClosureStatusType.NO_CLOSURES : ClosureStatusType.CLOSED;
    }
    return this.relevantClosure()?.status ?? ClosureStatusType.NO_CLOSURES;
  });

  /**
   * Whether the tag should be shown.
   * For "client" variant: only show when status is CLOSED (not for NO_CLOSURES or UPCOMING).
   * For "admin" variant: always show.
   */
  readonly shouldShow = computed(() => {
    if (this.variant() === "client") {
      return this.status() === ClosureStatusType.CLOSED;
    }
    return true;
  });

  readonly label = computed((): string => {
    const status = this.status();
    if (this.variant() === "client" && status === ClosureStatusType.CLOSED && !this.isMeetingRoom()) {
      // For CLOSED status in the client variant, use level-specific label (e.g., "Area closed", "Floor closed")
      // except for resource-level closures which just show "Closed"
      const policy = this.relevantClosure()?.policy;
      const level = getClosureLevelFromPolicy(policy);
      if (level && level !== "resource") {
        const levelLabel = getClosureLabelFromPolicy(policy);
        if (levelLabel) return levelLabel;
      }
    }
    return getClosureStatusLabel(status);
  });

  readonly color = computed((): TagColor => {
    if (this.variant() === "client") {
      return "gray";
    }
    return CLOSURE_STATUS_COLORS[this.status()];
  });

  readonly iconUrl = computed(() => {
    if (this.variant() === "client") {
      return "/assets/icons/closed.svg";
    }
    return "";
  });

  readonly tooltipText = computed(() => {
    const closure = this.relevantClosure();
    if (!closure) {
      return "";
    }

    const includeHiddenFromUsersText = this.variant() === "admin";
    return buildClosureTooltipText(closure.tooltipInfo, {
      includeHiddenFromUsersText,
    });
  });

  /** The most relevant closure (current or upcoming, or overlapping with period if specified) */
  private readonly relevantClosure = computed(() => {
    const start = this.periodStart();
    const end = this.periodEnd();

    // If period is provided, use period-based checking
    if (start !== undefined && end !== undefined) {
      return getMostRelevantClosureForPeriod(this.closures(), start, end, this.timezone());
    }

    // Otherwise, use current "now" based checking
    return getMostRelevantClosure(this.closures(), this.timezone());
  });
}
